import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import "@/i18n/detect";  // detects & persists browser language before i18n initialises
import "@/i18n";
import { Toaster } from "sonner";
import HomePage from "@/pages/HomePage";
import NeuNexusHome from "@/pages/NeuNexusHome";
import { PartnersIndex, PartnerProfile } from "@/pages/partners/PartnersPages";
import { LangProvider } from "@/i18n/neunexus";
import EcosystemPage from "@/pages/EcosystemPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ContactPage from "@/pages/ContactPage";
import AdminPage from "@/pages/AdminPage";
import FinancePage from "@/pages/FinancePage";
import BioPintoPage from "@/pages/BioPintoPage";
import CaboVerdeHubPage from "@/pages/CaboVerdeHubPage";
import PressKitPage from "@/pages/PressKitPage";
import BlueprintPage from "@/pages/BlueprintPage";
import AutomotiveBlueprintPage from "@/pages/AutomotiveBlueprintPage";
import StrategicReport from "@/pages/StrategicReport";
import BsgotechHome from "@/pages/BsgotechHome";
import CaboVerdePage from "@/pages/CaboVerdePage";
import RenalixPage from "@/pages/RenalixPage";
import ReportsIndex from "@/pages/ReportsIndex";
import OpsCenter from "@/pages/ops";
import OpsGate from "@/pages/ops/OpsGate";
import MiongaPage from "@/pages/mionga";
import NeuNexusPage from "@/pages/neunexus";
import NeuGreenPage from "@/pages/neugreen";
import OurEcosystem from "@/pages/OurEcosystem";
import ManualIndex from "@/pages/ManualIndex";
import LeadershipPage from "@/pages/LeadershipPage";
import IvoryToursPresentation from "@/pages/presentations/IvoryToursPresentation";
import { SEOHead } from "@/components/hx/SEOHead";

function ScrollToHash() {
  const location = useLocation();
  React.useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [location.hash, location.pathname]);
  return null;
}

// HX Ecosystems pillar slugs (kept for reference; routes are explicit below)

function App() {
  return (
    <LangProvider>
    <BrowserRouter>
      <ScrollToHash />
      <SEOHead />
      <Routes>
        {/* HannoNexus core routes */}
        <Route path="/" element={<BsgotechHome />} />
        <Route path="/cabo-verde" element={<CaboVerdePage />} />
        <Route path="/our-ecosystem" element={<OurEcosystem />} />
        <Route path="/manual" element={<ManualIndex />} />
        <Route path="/leadership" element={<LeadershipPage />} />
        <Route path="/neunexus" element={<NeuNexusHome />} />
        <Route path="/renalix" element={<RenalixPage />} />
        <Route path="/partners" element={<PartnersIndex />} />
        <Route path="/partners/:slug" element={<PartnerProfile />} />
        <Route path="/legacy-home" element={<HomePage />} />
        <Route path="/hx-energy" element={<EcosystemPage slug="hx-energy" />} />
        <Route path="/hx-automotive" element={<EcosystemPage slug="hx-automotive" />} />
        <Route path="/hx-systems" element={<EcosystemPage slug="hx-systems" />} />
        <Route path="/hx-infrastructure" element={<EcosystemPage slug="hx-infrastructure" />} />
        <Route path="/hx-academy" element={<EcosystemPage slug="hx-academy" />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/biopintostp" element={<BioPintoPage />} />
        <Route path="/projects/cabo-verde-hub" element={<CaboVerdeHubPage />} />
        <Route path="/press-kit" element={<PressKitPage />} />
        <Route path="/blueprint" element={<BlueprintPage />} />
        <Route path="/blueprint-automotive" element={<AutomotiveBlueprintPage />} />
        <Route path="/report" element={<StrategicReport />} />
        <Route path="/reports" element={<ReportsIndex />} />
        <Route path="/mou" element={<Navigate to="/ops" replace />} />
        <Route path="/ops" element={<OpsGate><OpsCenter /></OpsGate>} />
        <Route path="/mionga" element={<MiongaPage />} />
        <Route path="/neunexus/pitch" element={<NeuNexusPage />} />
        <Route path="/neugreen" element={<NeuGreenPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/presentations/ivory-tours" element={<IvoryToursPresentation />} />

        {/* Private tools */}
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/finance" element={<FinancePage />} />

        {/* Legacy redirects — old HX Ecosystems corporate routes */}
        <Route path="/solutions" element={<Navigate to="/hx-energy" replace />} />
        <Route path="/technology" element={<Navigate to="/hx-systems" replace />} />
        <Route path="/command-center" element={<Navigate to="/hx-systems" replace />} />
        <Route path="/industries" element={<Navigate to="/projects" replace />} />
        <Route path="/compliance" element={<Navigate to="/" replace />} />
        <Route path="/about" element={<Navigate to="/" replace />} />
        <Route path="/nexis" element={<Navigate to="/hx-systems" replace />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "rgba(10,15,26,0.90)",
            border: "1px solid rgba(10,155,95,0.2)",
            color: "#E6EAF0",
          },
        }}
      />
    </BrowserRouter>
    </LangProvider>
  );
}

export default App;
